import React from "react";
import { View, Text, Image, StyleSheet, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import routes from "../../../navigation/routes";
import { Reveal, WebClass, FONT_DISPLAY, FONT_MONO } from "./_shared";

// =============================================================================
// Impact Studies — three hand-picked customer stories with a hard metric on
// each. One per vertical (cruise / defense / hospitality) so the section
// reads as proof across the three industries we feature in NtVoxcraftIndustries.
//
// Each entry routes to the existing impact-study controller via routes.impactstudy
// with the matching `type` param (controller already handles these types).
// =============================================================================

const STUDIES = [
  {
    id: "ncl",
    type: "ncl",
    badge: "CRUISE",
    badgeKind: "cruise",
    logo: require("../../../assets/images/partners/logo_ncl.png"),
    logoTileDark: true, // NCL logo is white-on-transparent, needs a dark tile
    metric: "+26%",
    metricLabel: "comm-package\nsales spike",
    name: "Norwegian Cruise Line",
    desc:
      "Voxcraft telecom built into the Cruise Norwegian app across the 17-ship fleet. Deployed in one year, ahead of a two-year schedule, under budget.",
  },
  {
    id: "navy-csg",
    type: "navi-carrier-strike-group",
    badge: "DEFENSE",
    badgeKind: "defense",
    logo: null, // No Navy logo asset yet — render text wordmark instead
    metric: "90 days",
    metricLabel: "rapid\ndeployment",
    name: "U.S. Navy · Carrier Strike Group Four",
    desc:
      "Dual VoIP SIP with high availability + NIST SP 800-171 compliance across four TSVs. Enhanced joint and coalition interoperability.",
  },
  {
    id: "holand",
    type: "holand",
    badge: "HOSPITALITY",
    badgeKind: "hosp",
    logo: require("../../../assets/images/partners/logo-holland-america-group-new.png"),
    logoTileDark: true, // HAP PNG ships with a baked-in navy bg, so it only reads cleanly on a dark tile
    metric: "4,000+",
    metricLabel: "extensions across\n9 hotels",
    name: "Princess Lodges & Westmark Hotels",
    desc:
      "Unified platform across nine Alaska-based hotels. Housekeeping, room service, and Northern Lights alerts. Hardened for harsh winter operating conditions.",
  },
];

const BADGE_COLORS = {
  cruise:  { bg: "rgba(37,112,189,0.16)", border: "rgba(37,112,189,0.5)" },
  defense: { bg: "rgba(241,178,80,0.14)", border: "rgba(241,178,80,0.5)" },
  hosp:    { bg: "rgba(76,183,130,0.14)", border: "rgba(76,183,130,0.5)" },
};

export default function NtVoxcraftImpactStudies() {
  const { theme } = useTheme();
  const nav = useNavigation();
  const isMobile = useMobile();

  const goStudy = (type) => nav.navigate(routes.impactstudy, { type });

  // Map badgeKind → industry color from the theme tokens.
  const accentFor = (kind) =>
    kind === "defense" ? theme.orange : kind === "hosp" ? theme.green : theme.brand2;

  return (
    <View nativeID="impact" style={[styles.section, { paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={styles.container}>
        <Reveal>
          <View style={styles.head}>
            <Text style={[styles.eyebrow, { color: theme.textMute }]}>REAL DEPLOYMENTS · IMPACT STUDIES</Text>
            <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>
              Same platform.{"\n"}Three proof points.
            </Text>
            <Text style={[styles.lede, { color: theme.textDim }]}>
              Cruise revenue, defense compliance, hospitality scale. Voxcraft ships in production across all
              three. Numbers below are real outcomes from recent deployments.
            </Text>
          </View>
        </Reveal>

        <View style={[styles.grid, isMobile && styles.gridMobile]}>
          {STUDIES.map((s, i) => {
            const accent = accentFor(s.badgeKind);
            const badge = BADGE_COLORS[s.badgeKind];
            return (
              <View key={s.id} style={[styles.cell, isMobile && styles.cellMobile]}>
                <Reveal delay={(i % 3) + 1} style={styles.revealFill}>
                  <WebClass
                    className="voxcraft-ind-card"
                    style={[styles.card, { borderColor: theme.borderSoft, backgroundColor: theme.surface }]}
                  >
                    <Pressable
                      onPress={() => goStudy(s.type)}
                      style={styles.cardInner}
                    >
                      <View style={styles.topRow}>
                        <LogoTile theme={theme} entry={s} />
                        <View style={[styles.badge, { backgroundColor: badge.bg, borderColor: badge.border }]}>
                          <Text style={[styles.badgeText, { color: accent }]}>{s.badge}</Text>
                        </View>
                      </View>

                      <View style={styles.metricRow}>
                        <Text style={[styles.metric, { color: accent }]}>{s.metric}</Text>
                        <Text style={[styles.metricLabel, { color: theme.textDim }]}>{s.metricLabel}</Text>
                      </View>

                      <Text style={[styles.name, { color: theme.text }]}>{s.name}</Text>
                      <Text style={[styles.desc, { color: theme.textDim }]}>{s.desc}</Text>

                      <View style={[styles.linkWrap, { borderTopColor: theme.borderSoft }]}>
                        <Text style={[styles.link, { color: accent }]}>Read the impact study →</Text>
                      </View>
                    </Pressable>
                  </WebClass>
                </Reveal>
              </View>
            );
          })}
        </View>

        <Reveal>
          <View style={styles.viewallRow}>
            <Pressable
              onPress={() => nav.navigate(routes.impactstudies)}
              style={({ pressed }) => [
                styles.viewall,
                { borderColor: theme.borderSoft, backgroundColor: theme.surface },
                pressed && styles.viewallPressed,
              ]}
            >
              <Text style={[styles.viewallText, { color: theme.textDim }]}>
                View all 12 impact studies →
              </Text>
            </Pressable>
          </View>
        </Reveal>
      </View>
    </View>
  );
}

// =============================================================================
// LogoTile — small light-bg (or dark-bg) holder for the partner logo so the
// real PNG renders in its native colors. Renders a text wordmark when no logo
// asset is provided (e.g. U.S. Navy).
// =============================================================================
function LogoTile({ theme, entry }) {
  const tileBg = entry.logoTileDark ? theme.bg2 : "#f1f2f4";
  const tileBorder = entry.logoTileDark ? theme.borderSoft : "transparent";
  return (
    <View style={[styles.logoTile, { backgroundColor: tileBg, borderColor: tileBorder, borderWidth: entry.logoTileDark ? 1 : 0 }]}>
      {entry.logo ? (
        <Image source={entry.logo} style={styles.logoImg} resizeMode="contain" />
      ) : (
        <View style={styles.wmStack}>
          <Text style={styles.wmLine1}>U.S. NAVY</Text>
          <Text style={styles.wmLine2}>CSG · FOUR</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28 },
  container: { maxWidth: 1280, marginHorizontal: "auto", width: "100%" },
  head: { maxWidth: 760, marginBottom: 56 },
  eyebrow: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 1.6, marginBottom: 12, textTransform: "uppercase" },
  title: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46, marginBottom: 16 },
  titleMobile: { fontSize: 32, lineHeight: 36 },
  lede: { fontSize: 17, lineHeight: 27 },

  grid: { flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", marginHorizontal: -9 },
  gridMobile: { flexDirection: "column", marginHorizontal: 0 },
  cell: { width: "33.33%", padding: 9 },
  cellMobile: { width: "100%", padding: 0, paddingBottom: 12 },
  // Reveal needs flex:1 to fill the cell so card heights match across the row.
  revealFill: { flex: 1, width: "100%" },

  card: { flex: 1, borderRadius: 14, borderWidth: 1, overflow: "hidden" },
  cardInner: { flex: 1, padding: 28 },

  topRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 24 },

  logoTile: {
    height: 56,
    minWidth: 120,
    paddingHorizontal: 14, paddingVertical: 8,
    borderRadius: 10,
    alignItems: "center", justifyContent: "center",
  },
  logoImg: { width: 100, height: 40 },
  wmStack: { alignItems: "center" },
  wmLine1: { fontFamily: FONT_DISPLAY, fontWeight: "800", fontSize: 16, letterSpacing: 3, color: "#131417" },
  wmLine2: { fontFamily: FONT_MONO, fontWeight: "500", fontSize: 9, letterSpacing: 1.6, color: "#555", marginTop: 2 },

  badge: { paddingHorizontal: 11, paddingVertical: 5, borderRadius: 999, borderWidth: 1 },
  badgeText: { fontFamily: FONT_MONO, fontSize: 10, fontWeight: "700", letterSpacing: 1.2, textTransform: "uppercase" },

  metricRow: { flexDirection: "row", alignItems: "baseline", marginBottom: 14, gap: 8 },
  metric: { fontFamily: FONT_DISPLAY, fontSize: 44, fontWeight: "800", letterSpacing: -1.6, lineHeight: 44 },
  metricLabel: { fontSize: 13, lineHeight: 18, flex: 1 },

  name: { fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: "700", letterSpacing: -0.2, marginBottom: 8 },
  desc: { fontSize: 13.5, lineHeight: 21, marginBottom: 18, flex: 1 },

  linkWrap: { borderTopWidth: 1, paddingTop: 14 },
  link: { fontSize: 13, fontWeight: "600" },

  viewallRow: { alignItems: "center", marginTop: 40 },
  viewall: {
    flexDirection: "row", alignItems: "center",
    paddingHorizontal: 22, paddingVertical: 12,
    borderRadius: 8, borderWidth: 1,
  },
  viewallText: { fontSize: 14, fontWeight: "600" },
  viewallPressed: { opacity: 0.85, transform: [{ translateY: -1 }] },
});
